import { Injectable, Injector } from '@angular/core';
import { MetaReducer, Action, ActionReducer } from '@ngrx/store';
import { LoggerService } from './logger.service';
import { cloneDeep } from 'lodash';

interface ActionWithPayload extends Action {
  payload?: any;
}
/**
 * Service to be injected as a meta reducer to handle logging state changes on Redux Actions
 *
 * @export
 * @class StoreLoggerService
 */
@Injectable()
export class StoreLoggerService {
  /**
   * Set this to true to log all store actions
   *
   * @static
   * @memberof StoreLoggerService
   */
  public static logAllStoreActions = false;

  private logger: LoggerService;

  /**
   * Creates an instance of StoreLoggerService.
   * @param {Injector} injector
   * @memberof StoreLoggerService
   */
  constructor(injector: Injector) {
    // Need to inject after a timeout to get around cyclical dependencies
    setTimeout(() => {
      const loggerService = injector.get(LoggerService);
      this.logger = loggerService.instance('StoreLogger');
    });
  }

  /**
   * Returns boolean to see if the update-reducers action is called, this is used
   * to filter them out, as the store dev tools triggers this very often (for every
   * previous action).
   *
   * @private
   * @param {string} actionType
   * @returns {boolean} true if it does include update-reducers
   */
  private isNgrxRelatedLog(actionType: string = ''): boolean {
    return actionType.includes('@ngrx/store/update-reducers');
  }

  /**
   * Decides if this service should actually log
   * It will log if logAllStoreActions is set, or the debug flag is set
   *
   * @private
   * @returns {boolean} true if it should log
   * @memberof StoreLoggerService
   */
  private shouldLog(): boolean {
    return !!this.logger;
  }

  /**
   * Logs the given state and action if neccessary
   *
   * @param {*} state
   * @param {Action} action
   * @memberof StoreLoggerService
   */
  public logAction(_state, action: ActionWithPayload): void {
    if (this.shouldLog() && !this.isNgrxRelatedLog(action.type)) {
      this.logger.debug(`Extended State Logging: ${action.type}`, {
        payload: cloneDeep(action.payload),
        action: action.type
        // state: state // State gets to big, which prevents logs to be pushed to new relic.
      });
    }
  }
}

/**
 * Wraps the reducer with the log action from the service
 *
 * @export
 * @param {StoreLoggerService} storeLogger
 * @returns {MetaReducer<{}, Action>}
 */
export function getActionLoggingReducer(
  storeLogger: StoreLoggerService
): MetaReducer<{}, ActionWithPayload> {
  return function(reducer: ActionReducer<any>): ActionReducer<any> {
    return function(state, action: ActionWithPayload): {} {
      try {
        storeLogger.logAction(state, action);
      } catch (error) {
        // ignore
      }
      return reducer(state, action);
    };
  };
}
